import { useParams } from 'react-router';
import AppNavLink from '@/components/Router/AppNavLink';
import { formConfigurationMapping, type TFormConfig } from '@/features/ApplicationForm/FormConfig';
import { useApplicationForm } from '@/hooks/ReactQueryHooks/useApplicationFromQuery';
import { usePermission } from '@/hooks/usePermission';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import { EPermission } from '@/types/Permission';

interface IApplicationNavItems {
  name: 'Dashboard' | 'Applications' | 'Resource Center' | 'Form Settings' | 'Notifications';
  path: 'dashboard' | 'applications' | 'resources' | 'settings' | 'notifications';
}

let applicationNavItems: IApplicationNavItems[] = [
  { name: 'Dashboard', path: 'dashboard' },
  { name: 'Applications', path: 'applications' },
  { name: 'Resource Center', path: 'resources' },
  { name: 'Form Settings', path: 'settings' },
  { name: 'Notifications', path: 'notifications' },
];

// Map navigation item paths to AppRoute keys
const navPathToRouteKey: Record<IApplicationNavItems['path'], AppRoutes> = {
  dashboard: AppRoutes.ROVER_APPLICATION_FORM_DASHBOARD,
  applications: AppRoutes.ROVER_APPLICATION_FORM_APPLICATIONS,
  resources: AppRoutes.ROVER_APPLICATION_FORM_RESOURCES,
  settings: AppRoutes.ROVER_APPLICATION_FORM_SETTINGS,
  notifications: AppRoutes.ROVER_APPLICATION_NOTIFICATION_SETTINGS,
};

export const ApplicationNav = () => {
  const { applicationFormId } = useParams<string>();
  const { hasPermission } = usePermission();

  const fetchApplicationForm = useApplicationForm({
    formId: applicationFormId || '',
  });
  const applicationForm = fetchApplicationForm?.data?.data;

  const applicationFromConfig = formConfigurationMapping.find((fcm) => fcm.formCode === applicationForm?.templateCode) as TFormConfig;

  if (
    (applicationFromConfig && !applicationFromConfig.applicationDashboardComponent) ||
    !hasPermission([EPermission.READ_APPLICATION_FORM_DASHBOARD])
  ) {
    applicationNavItems = applicationNavItems.filter((n) => n.path !== 'dashboard');
  }

  if (!hasPermission([EPermission.READ_APPLICATION_FORM])) {
    applicationNavItems = applicationNavItems.filter((n) => n.path !== 'settings');
  }

  if (!hasPermission([EPermission.LIST_APPLICATIONS])) {
    applicationNavItems = applicationNavItems.filter((n) => n.path !== 'applications');
  }

  if (!hasPermission([EPermission.LIST_APPLICATION_FORM_RESOURCES])) {
    applicationNavItems = applicationNavItems.filter((n) => n.path !== 'resources');
  }

  return (
    <div className='border-b-2 border-neutralColor-light flex gap-8 my-5'>
      {applicationNavItems.map((item: IApplicationNavItems) => (
        <AppNavLink
          key={item.path}
          to={buildRoute(navPathToRouteKey[item.path], { applicationFormId: applicationFormId || '' })}
          baseClassName='px-2 font-semibold'
          activeClassName='border-primaryColor-normal text-primaryColor-normal border-b-2 pb-2 -mb-0.5'
        >
          {item.name}
        </AppNavLink>
      ))}
    </div>
  );
};
